import { useCallback, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { OnDocumentLoadSuccess } from "react-pdf/dist/esm/shared/types.js";

import LoadingComponent from "@components/shared/loading/loadingComponent";
import { getTranslateByKey } from "@/main/services/utils";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import * as S from "./styles";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

interface AnnouncementPdfViewerProps {
	readonly pdfBase64: string;
	readonly containerRef: any;
	readonly setReadComplete: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function AnnouncementPdfViewer({
	pdfBase64,
	containerRef,
	setReadComplete,
}: AnnouncementPdfViewerProps) {
	const [totalPages, setTotalPages] = useState(0);

	const handleDocumentLoadSuccess: OnDocumentLoadSuccess = ({ numPages }: any) => {
		setTotalPages(numPages);
	};

	const handleScroll = useCallback(() => {
		if (containerRef.current && totalPages > 0) {
			const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

			if (scrollHeight === 0) return;

			if (scrollTop + clientHeight >= scrollHeight - 50) {
				setReadComplete(true);
			}
		}
	}, [containerRef, setReadComplete, totalPages]);

	useEffect(() => {
		const containerElement = containerRef.current;

		if (containerElement) {
			containerElement.addEventListener("scroll", handleScroll);
			return () => {
				containerElement.removeEventListener("scroll", handleScroll);
			};
		}
	}, [containerRef, handleScroll]);

	useEffect(() => {
		if (totalPages > 0) {
			const timer = setTimeout(handleScroll, 150);
			return () => clearTimeout(timer);
		}
	}, [totalPages, handleScroll]);

	return (
		<S.PDFContainer ref={containerRef} id="announcement-pdf-scroll">
			<Document
				file={`data:application/pdf;base64,${pdfBase64}`}
				onLoadSuccess={handleDocumentLoadSuccess}
				loading={<LoadingComponent />}
				error={
					<h5 style={{ textAlign: "center", padding: 20 }}>{getTranslateByKey("information.failedLoadFile", true)}</h5>
				}
			>
				{Array.from(new Array(totalPages), (_, index) => (
					<Page
						key={`page_${index + 1}`}
						pageNumber={index + 1}
						renderTextLayer={false}
						renderAnnotationLayer={false}
						scale={1.2}
						onRenderSuccess={index === totalPages - 1 ? handleScroll : undefined}
					/>
				))}
			</Document>
		</S.PDFContainer>
	);
}
